import type { Calendar } from "./types.js";

/** The provider sync cursor fields on a Calendar. These are DEVICE+CALENDAR-
 *  local: a peer device's `events.list` syncToken / poll timestamps are
 *  meaningless here, so they must never leave the device (no backup, no
 *  multi-device sync) and an incoming copy must never clobber the local one.
 *  (`last_push_at` used to live here too; outbound dirtiness is now the per-row
 *  `mut > google_acked_mut` fact, so the cursor is gone.) */
export const CALENDAR_CURSOR_FIELDS = [
  "sync_token",
  "last_synced_at",
] as const;

/** Project a Calendar for export/transmission: null the device-local cursor
 *  fields so they never land in a backup file or the multi-device sync graph. */
export function clearSyncCursor(cal: Calendar): Calendar {
  return { ...cal, sync_token: null, last_synced_at: null };
}

/** When applying an INCOMING calendar (from sync/restore), keep the LOCAL cursor
 *  fields so a transmitted (always-null) copy can't wipe this device's cursor.
 *  `local` null = no existing row, so the incoming (null cursor) stands. */
export function preserveSyncCursor(incoming: Calendar, local: Calendar | null): Calendar {
  if (!local) return incoming;
  return {
    ...incoming,
    sync_token: local.sync_token,
    last_synced_at: local.last_synced_at,
  };
}
